/**
 * useTeamCommsSearch Hook
 * 
 * Unified search across Team Comms:
 * - Team Feed: posts, comments (via backend API)
 * - Messages: conversations and message content (via Firestore)
 * 
 * Features:
 * - Debounced search input
 * - Combined results with type indicators
 * - Snippet generation with context
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  limit
} from 'firebase/firestore';
import { getFirestoreDb } from '@/lib/firebase';
import { COLLECTIONS } from '@/lib/firestore/schema';
import { getConversationDisplayName } from '@/lib/firestore';
import { API_URL } from '../utils/constants';
import { stripHtmlFast } from '@/utils/html';

const db = () => getFirestoreDb();

// Configuration
const DEBOUNCE_DELAY = 300;
const MAX_FEED_RESULTS = 15;
const MAX_MESSAGE_RESULTS = 15;

/**
 * Get a snippet of text around the search match
 */
function getSnippet(text, searchQuery, contextLength = 50) {
  if (!text || !searchQuery) return stripHtmlFast(text) || '';
  
  // Strip HTML before processing
  const cleanText = stripHtmlFast(text);
  
  const lowerText = cleanText.toLowerCase();
  const lowerQuery = searchQuery.toLowerCase();
  const matchIndex = lowerText.indexOf(lowerQuery);
  
  if (matchIndex === -1) return cleanText.slice(0, contextLength * 2);
  
  const start = Math.max(0, matchIndex - contextLength);
  const end = Math.min(cleanText.length, matchIndex + searchQuery.length + contextLength);
  
  let snippet = cleanText.slice(start, end);
  
  if (start > 0) snippet = '...' + snippet;
  if (end < cleanText.length) snippet = snippet + '...';
  
  return snippet;
}

/**
 * Search Team Feed (posts and comments) via backend API
 */
async function searchTeamFeed(searchQuery, userId, churchId) {
  if (!(searchQuery || '').trim() || searchQuery.length < 2 || !churchId) return [];
  
  try {
    const response = await fetch(
      `${API_URL}/api/comms/v3/search?query=${encodeURIComponent(searchQuery)}&church_id=${encodeURIComponent(churchId)}&viewer_id=${encodeURIComponent(userId)}`
    );
    
    if (!response.ok) return [];
    
    const data = await response.json();
    const results = [];
    
    // Transform posts
    const posts = data.posts || [];
    posts.slice(0, MAX_FEED_RESULTS).forEach(post => {
      results.push({
        id: post.id,
        type: 'post',
        postId: post.id,
        content: post.content || '',
        snippet: getSnippet(post.content, searchQuery),
        authorName: post.author?.name || post.author?.full_name || 'Unknown',
        authorAvatar: post.author?.avatar_url,
        createdAt: post.created_at,
        category: 'feed'
      });
    });
    
    // Transform comments
    const comments = data.comments || [];
    comments.slice(0, MAX_FEED_RESULTS).forEach(comment => {
      results.push({
        id: comment.id,
        type: 'comment',
        postId: comment.post_id,
        commentId: comment.id,
        content: comment.content || '',
        snippet: getSnippet(comment.content, searchQuery),
        authorName: comment.author?.name || comment.author?.full_name || 'Unknown',
        authorAvatar: comment.author?.avatar_url,
        createdAt: comment.created_at,
        category: 'feed'
      });
    });
    
    return results;
  } catch (err) {
    console.error('Error searching team feed:', err);
    return [];
  }
}

/**
 * Search Messages in Firestore across all conversations
 */
async function searchMessages(searchQuery, conversations, userId) {
  if (!(searchQuery || '').trim() || searchQuery.length < 2 || conversations.length === 0) {
    return [];
  }
  
  const lowerQuery = searchQuery.toLowerCase();
  const results = [];
  
  for (const conv of conversations) {
    try {
      const messagesRef = collection(db(), COLLECTIONS.CONVERSATIONS, conv.id, COLLECTIONS.MESSAGES);
      const q = query(
        messagesRef,
        orderBy('createdAt', 'desc'),
        limit(100)
      );
      
      const snapshot = await getDocs(q);
      
      snapshot.docs.forEach(doc => {
        const message = { id: doc.id, ...doc.data() };
        
        // Skip deleted messages
        if (message.deletedForEveryone || message.deletedFor?.includes(userId)) {
          return;
        }
        
        // Check if message text contains query
        if (message.text && message.text.toLowerCase().includes(lowerQuery)) {
          results.push({
            id: message.id,
            type: 'message',
            conversationId: conv.id,
            conversation: conv,
            message: message,
            content: message.text,
            snippet: getSnippet(message.text, searchQuery),
            authorName: message.senderName || 'Unknown',
            authorAvatar: message.senderAvatar,
            conversationName: getConversationDisplayName(conv, userId),
            createdAt: message.createdAt?.toDate?.() || message.createdAt,
            category: 'messages'
          });
        }
      });
      
      if (results.length >= MAX_MESSAGE_RESULTS) break;
    } catch (err) {
      console.error(`Error searching messages in conversation ${conv.id}:`, err);
    }
  }
  
  // Sort by timestamp (most recent first)
  results.sort((a, b) => {
    const timeA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt || 0);
    const timeB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt || 0);
    return timeB - timeA;
  });
  
  return results.slice(0, MAX_MESSAGE_RESULTS);
}

/**
 * Main hook for unified Team Comms search
 */
export function useTeamCommsSearch(userId, conversations = [], churchId = null) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [feedResults, setFeedResults] = useState([]);
  const [messageResults, setMessageResults] = useState([]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Perform search when debounced query changes
  useEffect(() => {
    if (!(debouncedQuery || '').trim() || debouncedQuery.length < 2) {
      setFeedResults([]);
      setMessageResults([]);
      setIsSearching(false);
      return;
    }

    const performSearch = async () => {
      setIsSearching(true);
      
      try {
        // Search both in parallel
        const [feedData, messageData] = await Promise.all([
          searchTeamFeed(debouncedQuery, userId, churchId),
          searchMessages(debouncedQuery, conversations, userId)
        ]);
        
        setFeedResults(feedData);
        setMessageResults(messageData);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setIsSearching(false);
      }
    };

    performSearch();
  }, [debouncedQuery, userId, conversations, churchId]);

  // Combined results
  const allResults = useMemo(() => {
    return [...feedResults, ...messageResults];
  }, [feedResults, messageResults]);

  // Check if search is active
  const isSearchActive = (searchQuery || '').trim().length >= 2;
  const hasResults = allResults.length > 0;

  // Clear search
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setDebouncedQuery('');
    setFeedResults([]);
    setMessageResults([]);
  }, []);

  return {
    searchQuery,
    setSearchQuery,
    isSearching,
    isSearchActive,
    feedResults,
    messageResults,
    allResults,
    hasResults,
    clearSearch
  };
}
